import React, { useState, useEffect } from "react";
import axios from "axios";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { SocketProvider } from "./contexts/SocketContext";
import { MccProvider } from "./contexts/MccContext";
import ProtectedRoute from "./components/ProtectedRoute";
import Login from "./pages/Login";
import NotAllowed from "./pages/NotAllowed";
import MainMenu from "./pages/MainMenu";
import BatchUpdateBalanceForm from "./components/BatchUpdateBalanceForm";
import MonthlyPayments from "./pages/MonthlyPayments"; // Import the new component
import BottomMenuMobile from "./components/BottomMenuMobile";
import DesktopSidebar from "./components/DesktopSidebar";
import Savings from "./pages/Savings";
import ShoppingList from "./pages/ShoppingList";
import Dashboard from "./pages/Dashboard";
import Management from "./pages/Management";
import ShoppingListSoloPage from "./pages/ShoppingListSoloPage";
import WishList from "./pages/WishList";
import WishListSoloPage from "./pages/WishListSoloPage";

import { useTranslation } from "react-i18next";

const AppContent = () => {
  const [accounts, setAccounts] = useState([]);
  const [savingAccounts, setSavingAccounts] = useState([]);
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const { authenticated, loading } = useAuth();

  const isMainMenu = location.pathname === "/";
  const isLoginPage = location.pathname === "/login";
  const isSoloPage =
    (location.pathname.startsWith("/shopping-list/") && location.pathname.split("/").length === 3) ||
    location.pathname.startsWith("/wish-list/shared/");

  useEffect(() => {
    i18n.changeLanguage("ua");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Only fetch accounts when authenticated
    if (authenticated && !loading) {
      fetchAccounts();
      fetchSavingAccounts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticated, loading]);

  const fetchAccounts = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/accounts`);
      setAccounts(response.data);
    } catch (error) {
      console.error("Error fetching accounts:", error);
    }
  };

  const fetchSavingAccounts = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/saving-accounts`);
      setSavingAccounts(response.data);
    } catch (error) {
      console.error("Error fetching saving accounts:", error);
    }
  };

  const getTitle = (t) => {
    const path = location.pathname;
    if (path.startsWith("/shopping-list/")) {
      return t("shoppingList");
    } else if (path.startsWith("/dashboard")) {
      // Provide specific titles for dashboard subsections
      const parts = path.split("/").filter(Boolean);
      const sub = parts[1] || "burndown-chart"; // parts[0] === 'dashboard'
      switch (sub) {
        case "burndown-chart":
          return t("burndown-chart");
        case "spending-history":
          return t("spendingHistory");
        case "spending-categories":
          return t("spendingCategories");
        default:
          return t("dashboard");
      }
    } else if (path.startsWith("/management")) {
      // Provide specific titles for management sub-sections
      const parts = path.split("/").filter(Boolean);
      const sub = parts[1] || "accounts"; // parts[0] === 'management'
      switch (sub) {
        case "accounts":
          return t("accountManagement");
        case "savings":
          return t("savingAccountManagement");
        case "saving-goals":
          return t("savingGoals");
        case "exchange-rates":
          return t("exchangeRates");
        case "allowed-users":
          return t("allowedUsers", "Користувачі");
        default:
          return t("management");
      }
    } else if (path.startsWith("/wish-list")) {
      return t("wishList");
    }
    switch (path) {
      case "/balance":
        return t("balance");
      case "/monthly-payments":
        return t("monthlyPayments");
      case "/savings":
        return t("savings");
      case "/shopping-list":
        return t("shoppingList");
      case "/exchange-rates":
        return t("exchangeRates");
      default:
        return "";
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-50 text-gray-900 dark:bg-dark-bg dark:text-dark-text font-sans transition-colors duration-200">
      {/* Desktop Sidebar - hidden on mobile, shown on lg+ */}
      {!isLoginPage && !isSoloPage && <DesktopSidebar />}
      
      <div className={`w-full flex-grow ${!isLoginPage && !isSoloPage ? "lg:pl-[276px]" : ""}`}>
        {!isMainMenu && !isSoloPage && !isLoginPage && <BottomMenuMobile />}
        <div className={`flex-grow ${!isMainMenu && !isSoloPage && !isLoginPage ? "pb-28 lg:pb-0" : ""}`}>
          {/* Per-page header */}
          {!isMainMenu && !isSoloPage && !isLoginPage && (
            <header className="px-6 py-6 lg:px-8">
              <div className="max-w-3xl lg:max-w-5xl mx-auto flex justify-between items-center">
                <h1 className="text-3xl font-heading font-bold text-gray-900 dark:text-white">{getTitle(t)}</h1>
                <div id="header-actions"></div>
              </div>
            </header>
          )}
          <main className="px-4 sm:px-6 lg:px-8">
            <Routes>
              <Route path="/not-allowed" element={<NotAllowed />} />
              <Route path="/login" element={<Login />} />
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <MainMenu accounts={accounts} savingAccounts={savingAccounts} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/balance"
                element={
                  <ProtectedRoute>
                    <BatchUpdateBalanceForm accounts={accounts} onUpdateSuccess={fetchAccounts} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/monthly-payments"
                element={
                  <ProtectedRoute>
                    <MonthlyPayments />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/savings"
                element={
                  <ProtectedRoute>
                    <Savings onSavingUpdateSuccess={fetchSavingAccounts} />
                  </ProtectedRoute>
                }
              />
              <Route path="/shopping-list/:id" element={<ShoppingListSoloPage />} />
              <Route
                path="/shopping-list"
                element={
                  <ProtectedRoute>
                    <ShoppingList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/*"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/wish-list"
                element={
                  <ProtectedRoute>
                    <WishList />
                  </ProtectedRoute>
                }
              />

              <Route path="/wish-list/shared/:shareableLink" element={<WishListSoloPage />} />
              <Route
                path="/management/*"
                element={
                  <ProtectedRoute>
                    <Management
                      accounts={accounts}
                      onAccountAdded={fetchAccounts}
                      onSavingAccountAdded={fetchSavingAccounts}
                      savingAccounts={savingAccounts}
                    />
                  </ProtectedRoute>
                }
              />
            </Routes>
          </main>
        </div>
      </div>
    </div>
  );
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <SocketProvider>
          <MccProvider>
            <ThemeProvider>
              <AppContent />
            </ThemeProvider>
          </MccProvider>
        </SocketProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
